import React, { FC, Fragment, useState } from 'react';
import { Reorder } from 'framer-motion';

import Conditional from '../../common/conditional';
import ImageEditor from '../../common/imageEditor';
import Modal from '../modal';
import ReplaceImageForm from '../image-replacement';
import ReorderItem from '../../common/reorderItem';
import UploadImage from './image';

import { PDFImageProps, UploadProps } from '../../../utils/models';
import { getIsImageFile, reArrangePhotoRecord } from '../../../utils/utilities';
import { deleteSingleFileFromServer } from '../../../services/aws';

import classNames from './upload.module.scss';
import toastService from '../../../services/toastService';

interface UploadPreviewProps {
	hideCaption: UploadProps['hideCaption'];
	files: PDFImageProps[];
	onChangeFiles: (files: PDFImageProps[]) => void;
	onRemoveFile: (file: PDFImageProps) => void;
	onChangeFile: (file: PDFImageProps) => void;
}

const UploadPreview: FC<UploadPreviewProps> = (props) => {
	const [fileToReplace, setFileToReplace] = useState<PDFImageProps | null>(null);
	const [fileToEdit, setFileToEdit] = useState<PDFImageProps | null>(null);
	const [fileToRemove, setFileToRemove] = useState<PDFImageProps | null>(null);

	const handleCaptionChange = (updatedCaption: string, file: PDFImageProps) => {
		const newFile = { ...file, caption: updatedCaption };
		props.onChangeFile(newFile);
	};

	const handleFileRemove = async () => {
		if (fileToRemove) {
			props.onRemoveFile(fileToRemove);
			setFileToRemove(null);
			
			deleteSingleFileFromServer(fileToRemove.caption);
		}
	};
	
	const handleFileReplacement = async (image: PDFImageProps) => {
		if (fileToReplace) {
			props.onChangeFile({
				refNo: fileToReplace.refNo,
				src: image.src,
				caption: image.caption,
				format: image.format,
			});

			setFileToReplace(null);
			deleteSingleFileFromServer(fileToReplace.caption);
		}
	};

	const handleCloseEditor = () => {
		setFileToReplace(null);
		setFileToEdit(null);
	};

	const handleOpenEditor = (image: PDFImageProps) => {
		if (!image.src) return toastService.error('No image attached to this!');
		setFileToEdit(image);
	};

	const handleMoveItems = (images: PDFImageProps[]) => {
		const rearrangedImages = reArrangePhotoRecord(images);
		props.onChangeFiles(rearrangedImages);
	};

	return (
		<Conditional isVisible={props.files.length > 0}>
			<Fragment>
				<Modal
					isVisible={!!fileToRemove}
					style={{ height: 'auto' }}
					onClose={() => setFileToRemove(null)}
				>
					<div className={classNames['upload-preview-confirm']}>
						<h2>
							<i className="bi bi-info-circle"></i>
							<span>Remove File?</span>
						</h2>

						<p>
							Are you sure you want to remove this file? Click 'Yes' to proceed.
						</p>

						<div className={classNames['upload-preview-confirm-actions']}>
							<button
								type="button"
								className={classNames['upload-preview-confirm-actions-yes']}
								onClick={handleFileRemove}
							>
								Yes, confirm
							</button>

							<button
								type="button"
								className={classNames['upload-preview-confirm-actions-no']}
								onClick={() => setFileToRemove(null)}
							>
								Cancel
							</button>
						</div>
					</div>
				</Modal>

				{fileToReplace && (
					<Modal isVisible style={{ height: 'auto' }} onClose={handleCloseEditor}>
						<ReplaceImageForm initialImage={fileToReplace} onSave={handleFileReplacement} />
					</Modal>
				)}

				{fileToEdit && (
					<Modal isVisible onClose={handleCloseEditor}>
						<ImageEditor
							image={fileToEdit}
							onEditImage={(file) => props.onChangeFile(file)}
							onCloseEditor={handleCloseEditor}
						/>
					</Modal>
				)}

				<Reorder.Group axis="y" className="list-unstyled mt-2" values={props.files} onReorder={handleMoveItems}>
					{props.files.map((item) => (
						<ReorderItem key={item.src} value={item}>
							<UploadImage
								isImage={getIsImageFile(item.format)}
								image={item}
								displayCaption={!props.hideCaption}
								onEditImage={() => handleOpenEditor(item)}
								onReplaceImage={() => setFileToReplace(item)}
								onDeleteImage={() => setFileToRemove(item)}
								onUpdateCaption={(caption) => handleCaptionChange(caption, item)}
							/>
						</ReorderItem>
					))}
				</Reorder.Group>
			</Fragment>
		</Conditional>
	);
};

export default React.memo(UploadPreview);
